import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

const floatingItems = [
  { emoji: "🎲", label: "Ζάρια", x: -120, y: -80, delay: 0 },
  { emoji: "♟️", label: "Πιόνια", x: 110, y: -90, delay: 0.1 },
  { emoji: "🪙", label: "Νομίσματα", x: -100, y: 40, delay: 0.2 },
  { emoji: "💎", label: "Ρουμπίνια", x: 120, y: 50, delay: 0.15 },
  { emoji: "🏆", label: "Τρόπαια", x: -40, y: -120, delay: 0.25 },
  { emoji: "🎯", label: "Imperium 1800", x: 50, y: -110, delay: 0.05 },
  { emoji: "🎰", label: "Φρουτάκια", x: -130, y: -10, delay: 0.3 },
  { emoji: "🃏", label: "Χαρτιά", x: 130, y: -20, delay: 0.12 },
  { emoji: "🎪", label: "Κρεμάλα", x: 0, y: 100, delay: 0.2 },
  { emoji: "🎵", label: "Jukebox", x: -60, y: 90, delay: 0.08 },
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function ZizanioMarketTeaser({ open, onClose }: Props) {
  const navigate = useNavigate();

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

          {/* Floating items */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden flex items-center justify-center">
            {floatingItems.map((item, i) => (
              <motion.div
                key={i}
                className="absolute text-3xl md:text-4xl"
                style={{ left: `calc(50% + ${item.x}px)`, top: `calc(50% + ${item.y}px)` }}
                initial={{ opacity: 0, scale: 0, rotate: -30 }}
                animate={{
                  opacity: [0, 1, 1, 0.7],
                  scale: [0, 1.3, 1, 1.1],
                  rotate: [0, 10, -5, 0],
                  y: [0, -8, 4, 0],
                }}
                transition={{
                  delay: item.delay,
                  duration: 2.5,
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "easeInOut",
                }}
              >
                {item.emoji}
              </motion.div>
            ))}
          </div>

          {/* Main card */}
          <motion.div
            className="relative z-10 bg-gradient-to-br from-[hsl(45,90%,55%)] via-[hsl(35,85%,50%)] to-[hsl(25,80%,45%)] rounded-3xl p-1 mx-4 max-w-sm w-full shadow-2xl"
            initial={{ scale: 0.5, rotate: -10, y: 60 }}
            animate={{ scale: 1, rotate: 0, y: 0 }}
            exit={{ scale: 0.5, rotate: 10, y: 60 }}
            transition={{ type: "spring", damping: 15, stiffness: 200 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[hsl(220,40%,13%)] rounded-[22px] p-6 text-center relative overflow-hidden">
              {/* Sparkle overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-transparent via-white/5 to-white/10 pointer-events-none" />

              {/* Close */}
              <button
                onClick={onClose}
                className="absolute top-3 right-3 text-white/40 hover:text-white/80 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>

              {/* Big store icon */}
              <motion.div
                className="text-6xl mb-3"
                animate={{ rotate: [0, -5, 5, 0], scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
              >
                🏪
              </motion.div>

              <h3 className="font-display text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[hsl(45,90%,65%)] to-[hsl(25,85%,55%)] mb-2">
                Zizanio Market
              </h3>

              <p className="text-white/60 text-sm font-body mb-4 leading-relaxed">
                Νομίσματα, ρουμπίνια, power-ups, skins, μαρκες<br />
                και εκπλήξεις σε περιμένουν!
              </p>

              {/* Mini showcase */}
              <div className="flex justify-center gap-3 mb-5">
                {["🪙", "💎", "🎲", "🏆", "🎨"].map((e, i) => (
                  <motion.div
                    key={i}
                    className="w-11 h-11 bg-white/10 rounded-xl flex items-center justify-center text-xl border border-white/10"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 + i * 0.08 }}
                    whileHover={{ scale: 1.2, rotate: 10 }}
                  >
                    {e}
                  </motion.div>
                ))}
              </div>

              {/* Lock message */}
              <motion.div
                className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-5"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <p className="text-white/80 font-display font-bold text-sm mb-1">
                  🔒 Μόνο για μέλη!
                </p>
                <p className="text-white/50 text-xs font-body">
                  Γράψου δωρεάν και απόκτησε πρόσβαση στο Zizanio Market 
                  με αποκλειστικά items για τα αγαπημένα σου παιχνίδια!
                </p>
              </motion.div>

              {/* CTA */}
              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <Button
                  onClick={() => {
                    onClose();
                    navigate("/auth");
                  }}
                  className="w-full rounded-full font-display font-bold text-base py-5 bg-gradient-to-r from-[hsl(45,90%,55%)] to-[hsl(25,85%,50%)] text-[hsl(220,40%,13%)] hover:brightness-110 shadow-lg"
                >
                  ✨ Γράψου Δωρεάν!
                </Button>
              </motion.div>

              <p className="text-white/30 text-[11px] font-body mt-3">
                Εγγραφή σε 30 δευτερόλεπτα · Χωρίς πιστωτική
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
